import { useEffect, useRef, useState } from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

// Hellopeter v2 carousel widget (the v1 api-v6 backend was retired Sep 2026).
// Business ID 1024955 = PropInvest. Config is base64 JSON read by the v2 script;
// colours carried over from our v1 customisation (brand green stars, grey text).
const HP_BUSINESS_ID = '1024955';
const HP_CONTAINER_ID = `hp-carousel-${HP_BUSINESS_ID}`;
const HP_CONFIG = btoa(
  JSON.stringify({
    dimension: 940,
    headerMode: 'metrics',
    showIndustryRank: true,
    lookAndFeel: 'original',
    transparentStyle: {
      fontColor: '#374151',
      borderColor: '#e5e7eb',
      bgColor: '#ffffff',
      useWhiteLogo: false,
    },
    starColors: {
      average: '#3d7a24',
      fiveStar: '#3d7a24',
      fourStar: '#3d7a24',
    },
    reviewCount: 30,
    anonymous: false,
    channels: ['hellopeter'],
  })
);

export default function TestimonialsSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [widgetFailed, setWidgetFailed] = useState(false);
  const section = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  // Defer Hellopeter widget loading until the section scrolls into view
  useEffect(() => {
    const target = containerRef.current;
    if (!target) return;

    let timeout: ReturnType<typeof setTimeout>;

    const loadWidget = () => {
      // Remove any existing HP widget script to allow re-init on navigation
      const existing = document.getElementById('hp-widget-code');
      if (existing) existing.remove();

      const script = document.createElement('script');
      script.async = true;
      script.id = 'hp-widget-code';
      script.setAttribute('data-hp-id', HP_CONTAINER_ID);
      script.src = 'https://cdn.hellopeter.com/widgets/v2/carousel.js';
      script.onerror = () => setWidgetFailed(true);

      document.head.appendChild(script);

      // Fallback timeout — the v2 widget renders into a shadow root on the
      // container; if none appears in 8 seconds, show the direct link instead
      timeout = setTimeout(() => {
        const widgetEl = document.getElementById(HP_CONTAINER_ID);
        if (widgetEl && !widgetEl.shadowRoot) {
          setWidgetFailed(true);
        }
      }, 8000);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadWidget();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(target);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
      const el = document.getElementById('hp-widget-code');
      if (el) el.remove();
    };
  }, []);

  return (
    <section className="section-padding bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200">
      <div ref={section.ref} className="section-container">
        {/* Custom heading */}
        <div className={`text-center mb-10 reveal ${section.isVisible ? 'visible' : ''}`}>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
            OUR TESTIMONIALS
          </h2>
          <div className="mt-3 h-1 w-16 bg-primary rounded mx-auto" />
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Don't just take our word for it — hear from real investors who've trusted PropInvest&#8482; with their property journey.
          </p>
        </div>

        {/* Trust badges row */}
        <div className={`flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-10 reveal ${section.isVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
          <div className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
            <ShieldCheck size={16} className="text-primary" />
            Verified Reviews
          </div>
          <span className="hidden md:block text-gray-300">|</span>
          <a
            href="https://www.hellopeter.com/propinvest"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-dark transition"
          >
            View on HelloPeter
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Widget wrapper — larger, with card frame and CSS overrides */}
        <div
          className={`hp-widget-wrapper max-w-[980px] mx-auto bg-white rounded-2xl border border-gray-200 shadow-lg p-4 md:p-6 reveal ${section.isVisible ? 'visible' : ''}`}
          style={{ transitionDelay: '0.2s' }}
        >
          <div
            ref={containerRef}
            id={HP_CONTAINER_ID}
            data-hp-widget="carousel"
            data-hp-business={HP_BUSINESS_ID}
            data-hp-config={HP_CONFIG}
          />
        </div>

        {widgetFailed && (
          <div className="text-center mt-8">
            <p className="text-gray-500 mb-3">Our reviews widget is loading slowly.</p>
            <a
              href="https://www.hellopeter.com/propinvest"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary-dark font-semibold transition"
            >
              See our reviews on HelloPeter
              <ExternalLink size={16} />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
